import { AUTHENTICATION_TYPE } from '../enums/authenticationType';

export interface IUserHydrationSubject {
    userId: string | null,
    authenticationType: AUTHENTICATION_TYPE | null,
}

/**
 * Anonymous and authenticated sessions can share a uid during auth upgrades, so
 * admin/access hydration must key off both uid and auth mode.
 */
export function didUserHydrationSubjectChange(
    previous: IUserHydrationSubject,
    next: IUserHydrationSubject,
): boolean {
    return previous.userId !== next.userId
        || previous.authenticationType !== next.authenticationType;
}

/**
 * User-derived caches should refresh when an auth handoff finishes with a real
 * signed-in Firebase user, but not for anonymous sessions or steady-state idle.
 */
export function shouldRefreshUserDerivedCachesAfterAuthHandoff(data: {
    wasSigningInOut: boolean,
    signingInOut: boolean,
    authUser: {
        isAnonymous?: boolean,
    } | null,
}): boolean {
    return data.wasSigningInOut
        && !data.signingInOut
        && data.authUser != null
        && data.authUser.isAnonymous !== true;
}

/**
 * Ping snapshots can briefly report permission errors during auth transitions.
 * Those should be ignored only while local auth state and Firebase auth are out
 * of sync; otherwise the error should still surface.
 */
export function shouldIgnoreTransientUserPingPermissionDenied(data: {
    errorCode: string,
    signingInOut: boolean,
    authUserId: string | null,
    localUserId: string | null,
}): boolean {
    return data.errorCode === 'permission-denied'
        && (
            data.signingInOut
            || data.authUserId == null
            || data.localUserId == null
            || data.authUserId !== data.localUserId
        );
}

/**
 * Ping bumps should fan out to user, post-access, and cart refreshes only when
 * they advance the observed version for a settled signed-in user.
 */
export function shouldProcessUserPingVersion(data: {
    signingInOut: boolean,
    hasUser: boolean,
    version: number,
    lastUserPingVersion: number,
}): boolean {
    return !data.signingInOut
        && data.hasUser
        && data.version > data.lastUserPingVersion;
}
